import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Search, UserCircle, ExternalLink, Mail, Phone, MapPin, Briefcase, Loader2
} from 'lucide-react';
import { getUsers } from '@/lib/api';
import { toast } from 'sonner';

export default function AdminUserSession() {
  // Handle both legacy admin and SaaS tenant contexts
  const outletContext = useOutletContext() || {};
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch users from the users collection (team members with unique_url)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        const response = await getUsers();
        // Filter to only include users with unique_url (passwordless access)
        const usersWithAccess = (response.data || []).filter(u => u.unique_url);
        setUsers(usersWithAccess);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        toast.error('Failed to load users');
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const filteredUsers = users?.filter(user => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.department?.name?.toLowerCase().includes(search) ||
      user.designation?.toLowerCase().includes(search)
    );
  }) || [];

  // Open user's actual profile URL in a new tab
  const handleViewUserProfile = (user) => {
    // Use the user's unique_url directly (e.g., "emily-14e59823")
    const userUrl = `/user/${user.unique_url}/`;
    
    // Open in new tab so admin can see exactly what the user sees
    window.open(userUrl, '_blank');
  };

  return (
    <div className="space-y-6" data-testid="admin-user-session">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <UserCircle className="h-6 w-6 text-primary" />
            <CardTitle>User Session View</CardTitle>
          </div>
          <CardDescription>
            View user profiles as they see them. Click on a user to open their profile in a new tab.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Search users by name, email, or department..." 
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <Skeleton className="h-14 w-14 rounded-full" />
                      <div className="space-y-2 flex-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-48" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <UserCircle className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="font-medium text-lg">No Users Found</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Team members are created from the Users page under &quot;Add User&quot;.
              </p>
            </div>
          )}

          {/* Users Grid */}
          {!isLoading && filteredUsers.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredUsers.map((user) => (
                <Card 
                  key={user.id} 
                  className="hover:shadow-lg hover:border-blue-300 transition-all group"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-14 w-14 border-2 border-slate-100">
                        <AvatarImage src={user.avatar_url} />
                        <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-lg">
                          {user.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 truncate">{user.name}</p>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground mt-0.5">
                          <Mail className="h-3 w-3" />
                          <span className="truncate">{user.email}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <Badge variant="secondary" className="text-xs">
                            {user.department?.name || 'No Dept'}
                          </Badge>
                          {user.is_active === false && (
                            <Badge variant="destructive" className="text-xs">Inactive</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* User Details */}
                    <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                      {user.designation && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Briefcase className="h-3 w-3" />
                          <span>{user.designation}</span>
                        </div>
                      )}
                      {user.location && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          <span>{user.location}</span>
                        </div>
                      )}
                      {user.phone_number && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Phone className="h-3 w-3" />
                          <span>{user.phone_number}</span>
                        </div>
                      )}
                    </div>

                    {/* Access URL Preview */}
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <span>Access URL:</span>
                        <code className="bg-muted px-1 rounded">/user/{user.unique_url}</code>
                      </div>
                    </div>

                    {/* View Profile Button */}
                    <Button 
                      className="w-full mt-4 group-hover:bg-blue-600"
                      onClick={() => handleViewUserProfile(user)}
                      disabled={!user.unique_url}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View User Profile
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
